/** Rep name normalization and cross-region dedupe for scope pickers. */

export interface RepRow {
  id: number;
  full_name: string;
  team_id: number;
}

export function normalizeRepName(name: string): string {
  return name.trim().replace(/\s+/g, " ").toLocaleLowerCase();
}

export function dedupeRepOptions(reps: RepRow[]): { id: number; name: string }[] {
  const byName = new Map<string, RepRow[]>();

  for (const rep of reps) {
    const key = normalizeRepName(rep.full_name);
    const group = byName.get(key) ?? [];
    group.push(rep);
    byName.set(key, group);
  }

  return Array.from(byName.values())
    .map((group) => {
      const canonical = group.reduce((best, rep) => (rep.id < best.id ? rep : best));
      return { id: canonical.id, name: canonical.full_name };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function expandRepSelection(
  selectedRepIds: number[],
  scopedReps: RepRow[],
): { keyRepId: number; label: string; repIds: number[]; teamIds: number[] }[] {
  const scopedById = new Map(scopedReps.map((rep) => [rep.id, rep]));
  const selectedNames = new Set<string>();

  for (const repId of selectedRepIds) {
    const rep = scopedById.get(repId);
    if (rep) selectedNames.add(normalizeRepName(rep.full_name));
  }

  return [...selectedNames]
    .map((nameKey) => {
      const matches = scopedReps.filter((row) => normalizeRepName(row.full_name) === nameKey);
      const canonical = matches.reduce((best, row) => (row.id < best.id ? row : best));
      return {
        keyRepId: canonical.id,
        label: canonical.full_name,
        repIds: matches.map((row) => row.id),
        teamIds: [...new Set(matches.map((row) => row.team_id))],
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function collectScopedReps(reps: RepRow[], allowedTeamIds: number[]): RepRow[] {
  if (allowedTeamIds.length === 0) return reps;
  const teamIdSet = new Set(allowedTeamIds);
  return reps.filter((rep) => teamIdSet.has(rep.team_id));
}
